
  import { defineComponent, PropType } from 'vue';
  import { mapActions } from 'vuex';
  import { useVuelidate } from '@vuelidate/core';
  import { required, requiredIf } from '@vuelidate/validators';
  import moment from 'moment';

  import ComponentInput from '@/common/components/ComponentInput.vue';
  import DateComponent from '@/common/components/DateComponent/DateComponent.vue';
  import Field from '@/common/components/Field.vue';
  import ButtonComponent from '@/common/components/redesigned/ButtonComponent.vue';
  import SelectMultiComponent from '@/common/components/SelectMultiComponent.vue';
  import Constants from '@/common/constants';
  import { TDictionary } from '@/common/types/dictionary';

  import ModalComponent from '@/components/modals/ModalComponent.vue';

  import { BASE_EVENTS_ORGANIZER } from '../constants/constants';
  import { EVENTS_VES_ACTIONS_NAME } from '../store/events/name';
  import { EventStatus, FEREventVedRequestDto, TEventCreateForm } from '../types/events';

  const DEFAULT_FORM: TEventCreateForm = {
    organizer: BASE_EVENTS_ORGANIZER,
    countryPartner: [],
    countryPartnerClarified: false,
    ferEventVedTypeId: null,
    startEventDate: '',
    endEventDate: '',
    eventLocation: '',
    eventLocationClarified: false,
    name: '',
    ferEventStatusId: null,
    description: '',
    russianDelegation: '',
    foreignDelegation: '',
    contactPerson: '',
  };

  // Для создания доступен ограниченный набор статусов по ТЗ.
  const ALLOWED_EVENT_STATUS_IDS = [EventStatus.Planned, EventStatus.Completed, EventStatus.InProgress];

  const DEFAULT_ERRORS = {
    countryPartner: false,
    ferEventVedTypeId: false,
    startEventDate: false,
    endEventDate: false,
    ferEventStatusId: false,
  };

  export default defineComponent({
    name: 'AddVESEventCard',
    components: { ComponentInput, DateComponent, ButtonComponent, Field, SelectMultiComponent, ModalComponent },
    emits: ['update:modelValue', 'saved'],
    props: {
      modelValue: {
        type: Boolean,
        default: false,
      },
      editId: {
        type: Number as PropType<number | null>,
        default: null,
      },
      countryOptions: {
        type: Array as PropType<TDictionary[]>,
        default: () => [],
      },
      eventTypeOptions: {
        type: Array as PropType<TDictionary[]>,
        default: () => [],
      },
      eventStatusOptions: {
        type: Array as PropType<TDictionary[]>,
        default: () => [],
      },
    },
    data(): {
      isSaving: boolean;
      isLoadingData: boolean;
      form: TEventCreateForm;
      fieldErrors: Record<string, boolean>;
    } {
      return {
        isSaving: false,
        isLoadingData: false,
        form: { ...DEFAULT_FORM },
        fieldErrors: {
          ...DEFAULT_ERRORS,
        },
      };
    },
    computed: {
      localValue: {
        get(): boolean {
          return this.modelValue;
        },
        set(value: boolean) {
          this.$emit('update:modelValue', value);
        },
      },
      isEditMode(): boolean {
        return !!this.editId;
      },
      modalTitle(): string {
        return this.isEditMode ? 'Редактирование мероприятия' : 'Создание мероприятия';
      },
      mappedCountryOptions(): TDictionary[] {
        return this.countryOptions.map((c) => ({ ...c, label: c.name }));
      },
      allowedEventStatusOptions(): TDictionary[] {
        return this.eventStatusOptions.filter((status) => ALLOWED_EVENT_STATUS_IDS.includes(status.id));
      },
      startDateLowerLimit(): Date {
        return moment().startOf('day').toDate();
      },
      endDateLowerLimit(): Date {
        return this.form.startEventDate
          ? moment(this.form.startEventDate).startOf('day').toDate()
          : this.startDateLowerLimit;
      },
    },
    watch: {
      modelValue(isOpen) {
        if (isOpen) {
          this.v$.$reset();
          if (this.isEditMode) this.loadEventData();
          else this.form = { ...DEFAULT_FORM };
        }
      },
      'form.countryPartnerClarified'(isClarified: boolean) {
        if (isClarified) {
          this.form.countryPartner = [];
          this.clearError('countryPartner');
        }
      },
      'form.eventLocationClarified'(isClarified: boolean) {
        if (isClarified) {
          this.form.eventLocation = '';
        }
      },
    },
    methods: {
      ...mapActions({
        createEvent: EVENTS_VES_ACTIONS_NAME.createEvent,
        updateEvent: EVENTS_VES_ACTIONS_NAME.updateEvent,
        getEventById: EVENTS_VES_ACTIONS_NAME.getEventById,
      }),

      async loadEventData() {
        try {
          this.isLoadingData = true;
          const res = await this.getEventById(this.editId);
          if (res?.data) this.populateForm(res.data);
        } catch {
          Constants.alert.fire(Constants.errorTitle, 'Не удалось загрузить данные мероприятия.', 'error');
          this.localValue = false;
        } finally {
          this.isLoadingData = false;
        }
      },

      populateForm(data: FEREventVedRequestDto & { id: number }) {
        const isCountryEmpty = !data.partnerCountry || data.partnerCountry.length === 0;
        const isLocationClarified = data.eventLocation === 'Уточняется';

        this.form = {
          id: data.id,
          organizer: BASE_EVENTS_ORGANIZER,
          countryPartnerClarified: isCountryEmpty,
          countryPartner: isCountryEmpty ? [] : data.partnerCountry.map((c) => c.id),
          ferEventVedTypeId: data.ferEventVedTypeId,
          startEventDate: data.startEventDate,
          endEventDate: data.endEventDate || '',
          eventLocationClarified: isLocationClarified,
          eventLocation: isLocationClarified ? '' : data.eventLocation || '',
          name: data.name || '',
          ferEventStatusId: data.ferEventStatusId,
          description: data.description || '',
          russianDelegation: data.russianDelegation || '',
          foreignDelegation: data.foreignDelegation || '',
          contactPerson: data.contactPerson || '',
        };
      },

      validate(): boolean {
        this.v$.$touch();
        const newErrors: Record<string, boolean> = { ...DEFAULT_ERRORS };
        let isValid = true;

        if (!this.form.countryPartnerClarified && this.form.countryPartner.length === 0) {
          newErrors.countryPartner = true;
          isValid = false;
        }
        if (!this.form.ferEventVedTypeId) {
          newErrors.ferEventVedTypeId = true;
          isValid = false;
        }
        if (!this.form.startEventDate) {
          newErrors.startEventDate = true;
          isValid = false;
        }
        if (
          this.form.startEventDate &&
          this.form.endEventDate &&
          new Date(this.form.startEventDate) > new Date(this.form.endEventDate)
        ) {
          newErrors.endEventDate = true;
          isValid = false;
        }
        if (!this.form.ferEventStatusId) {
          newErrors.ferEventStatusId = true;
          isValid = false;
        }

        if (this.v$.$invalid) isValid = false;

        this.fieldErrors = newErrors;
        return isValid;
      },

      buildRequest(): FEREventVedRequestDto {
        return {
          startEventDate: this.form.startEventDate,
          endEventDate: this.form.endEventDate || null,
          name: this.form.name || null,
          eventLocation: this.form.eventLocationClarified ? 'Уточняется' : this.form.eventLocation,
          description: this.form.description || null,
          russianDelegation: this.form.russianDelegation || null,
          foreignDelegation: this.form.foreignDelegation || null,
          contactPerson: this.form.contactPerson || null,
          ferEventStatusId: this.form.ferEventStatusId,
          ferEventVedTypeId: this.form.ferEventVedTypeId,
          significantFlag: false,
          partnerCountry: this.form.countryPartnerClarified ? [] : this.form.countryPartner.map((id) => ({ id })),
        };
      },

      clearError(field: string) {
        if (this.fieldErrors) this.fieldErrors[field] = false;
      },

      async onSubmit() {
        if (this.isSaving) return;
        if (!this.validate()) return;

        try {
          this.isSaving = true;
          const payload = this.buildRequest();

          const res = this.isEditMode
            ? await this.updateEvent({ id: this.form.id, data: payload })
            : await this.createEvent(payload);

          if (!res?.success) {
            Constants.alert.fire(Constants.errorTitle, 'Не удалось сохранить мероприятие', 'error');
            return;
          }

          Constants.alert.fire('Успех', Constants.commonSuccess, 'success');
          this.$emit('saved');
          this.localValue = false;
        } catch {
          Constants.alert.fire(Constants.errorTitle, 'Не удалось сохранить мероприятие', 'error');
        } finally {
          this.isSaving = false;
        }
      },
    },
    setup: () => ({ v$: useVuelidate() }),
    validations() {
      return {
        form: {
          eventLocation: { required: requiredIf(() => !this.form.eventLocationClarified) },
          name: { required },
        },
      };
    },
  });
